// src/store/ictRelatedDomainSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiClient from "./apiClient";

// FETCH: all related domain records for a given ICT
export const fetchRelatedDomainsByIctId = createAsyncThunk(
  "ictRelatedDomain/fetchByIctId",
  async (ictId, { rejectWithValue }) => {
    try {
      const res = await apiClient.get(`/related-ict-domain/ict/${ictId}`);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// INSERT: create new relation
// expected payload: { ictId, domainId, status: "A" }
export const insertRelatedDomain = createAsyncThunk(
  "ictRelatedDomain/insertRelatedDomain",
  async (data, { rejectWithValue }) => {
    try {
      const res = await apiClient.post(`/related-ict-domain/`, data);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// UPDATE: update relation by id
export const updateRelatedDomain = createAsyncThunk(
  "ictRelatedDomain/updateRelatedDomain",
  async ({ id, ...data }, { rejectWithValue }) => {
    try {
      const res = await apiClient.put(`/related-ict-domain/${id}`, data);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// PATCH STATUS: toggle A/D
export const patchRelatedDomainStatus = createAsyncThunk(
  "ictRelatedDomain/patchRelatedDomainStatus",
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const res = await apiClient.patch(`/related-ict-domain/${id}/status`, { status });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// DELETE: delete relation by id
export const deleteRelatedDomain = createAsyncThunk(
  "ictRelatedDomain/deleteRelatedDomain",
  async (id, { rejectWithValue }) => {
    try {
      const res = await apiClient.delete(`/related-ict-domain/${id}`);
      return res.data; // can be deleted record OR {id}; both handled in reducer
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

const ictRelatedDomainSlice = createSlice({
  name: "ictRelatedDomain",
  initialState: {
    relatedDomains: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // FETCH
      .addCase(fetchRelatedDomainsByIctId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRelatedDomainsByIctId.fulfilled, (state, action) => {
        state.loading = false;
        state.relatedDomains = action.payload;
      })
      .addCase(fetchRelatedDomainsByIctId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // INSERT
      .addCase(insertRelatedDomain.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(insertRelatedDomain.fulfilled, (state, action) => {
        state.loading = false;

        // If backend returns list => replace, else push single record
        if (Array.isArray(action.payload)) state.relatedDomains = action.payload;
        else state.relatedDomains.push(action.payload);
      })
      .addCase(insertRelatedDomain.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // UPDATE
      .addCase(updateRelatedDomain.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateRelatedDomain.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.relatedDomains.findIndex((r) => r.id === action.payload.id);
        if (idx !== -1) state.relatedDomains[idx] = action.payload;
      })
      .addCase(updateRelatedDomain.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // PATCH STATUS
      .addCase(patchRelatedDomainStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(patchRelatedDomainStatus.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.relatedDomains.findIndex((r) => r.id === action.payload.id);
        if (idx !== -1) state.relatedDomains[idx].status = action.payload.status;
      })
      .addCase(patchRelatedDomainStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // DELETE
      .addCase(deleteRelatedDomain.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteRelatedDomain.fulfilled, (state, action) => {
        state.loading = false;
        const deletedId = action.payload?.id ?? action.meta.arg;
        state.relatedDomains = state.relatedDomains.filter((r) => r.id !== deletedId);
      })
      .addCase(deleteRelatedDomain.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default ictRelatedDomainSlice.reducer;
